import {normalizeAccountTypes, SimpleAccountType} from '@client/services/transformers/account-transformers';
import {AccountSummaryDetailsResponse, ClientService, GetAccountSummaryOptions} from '@simpleinvest/ms-client-sdk';
// eslint-disable-next-line import/no-cycle
import {apiKey, getLogger} from '@client/services/sdk';
// eslint-disable-next-line import/no-cycle
import {
  requestInterceptorFulfilledHandler,
  requestInterceptorRejectedHandler,
  responseInterceptorFulfilledHandler,
  responseInterceptorRejectedHandler,
} from '@client/interceptors';
// eslint-disable-next-line import/no-cycle
import bffApi, {BffResponse} from '@client/bffApi';
import {AccountType, CustodianService, GetInfoOptions, CustodianInfo} from '@simpleinvest/ms-custodian-sdk';
import {memoize} from '@client/util/memoizer';
import {
  GetTargetPortfolioAtAccountLevelOptions,
  Portfolio,
  InvestmentService,
  GetCurrentPortfolioAtAccountLevelOptions,
} from '@simpleinvest/ms-investment-sdk';
import {HoldingsService, CurrentHoldingsResponse} from '@simpleinvest/ms-holdings-sdk';
import {SourcePlatform} from '@simpleinvest/ms-core-sdk';

export interface GetAvailableAccountTypesOptions {
  clUserId?: number;
  accountRequestId?: number;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const _getAvailableAccountTypes = async (options?: GetAvailableAccountTypesOptions): Promise<SimpleAccountType[]> => {
  const {clUserId, accountRequestId} = options || {};

  const {data} = await bffApi
    .getAxiosInstance()
    .get<BffResponse<AccountType[]>>(`${bffApi.getBaseUrl(true)}/clients/${clUserId}/account-types`, {
      params: {
        ...(clUserId ? {clUserId} : {}),
        ...(accountRequestId ? {accountRequestId} : {}),
      },
    });

  // TDOO: Remove this transformation and update everything downstream.
  return normalizeAccountTypes(data);
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const _getAvailableAccountTypesMemoized = memoize(_getAvailableAccountTypes, {
  id: 'getAccountTypes',
  resolver: (options?: GetAvailableAccountTypesOptions) => `${options?.clUserId}_${options?.accountRequestId}`,
});

export default {
  getClientServiceInstance(): ClientService {
    return new ClientService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  getHoldingsServiceInstance(): HoldingsService {
    return new HoldingsService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  async getHoldings(accountIds: string): Promise<CurrentHoldingsResponse[]> {
    const service: HoldingsService = await this.getHoldingsServiceInstance();

    return service.getCurrentHoldings({accountIds});
  },
  getAvailableAccountTypes(options?: GetAvailableAccountTypesOptions): Promise<SimpleAccountType[]> {
    return _getAvailableAccountTypesMemoized(options);
  },
  getCustodianServiceInstance({logger = getLogger()} = {}): CustodianService {
    return new CustodianService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger,
    });
  },
  async getCustodianInfo(options: GetInfoOptions): Promise<CustodianInfo[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getInfo(options);
  },
  async getAccountSummary(options: GetAccountSummaryOptions): Promise<AccountSummaryDetailsResponse[]> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.getAccountDetails(options);
  },
  getInvestmentServiceInstance({logger = getLogger()} = {}): InvestmentService {
    return new InvestmentService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger,
    });
  },
  async getTargetPortfolio(options: GetTargetPortfolioAtAccountLevelOptions): Promise<Portfolio[]> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();

    return service.getTargetPortfolioAtAccountLevel(options);
  },
  async getCurrentPortfolio(options: GetCurrentPortfolioAtAccountLevelOptions): Promise<Portfolio[]> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();

    return service.getCurrentPortfolioAtAccountLevel(options);
  },
};
